import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Loader2, LogIn } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { z } from "zod";

import { ApiError, authApi, setupApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";

const searchSchema = z.object({
  redirect: z.string().optional(),
  google: z.enum(["cancelled", "failed", "not_approved"]).optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: ({ context, search }) => {
    // Already logged in? Bounce back to wherever they were headed (or home).
    if (context.auth.status === "authenticated") {
      throw redirect({ to: search.redirect ?? "/" });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const setMe = useAuthStore((s) => s.setMe);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    setupApi.status().then((result) => setNeedsSetup(result.needs_setup)).catch(() => undefined);
  }, []);
  const googleError =
    search.google === "not_approved"
      ? "This Google email has not been approved by a Biz360 Admin, or the account is inactive."
      : search.google === "cancelled"
        ? "Google sign-in was cancelled."
        : search.google === "failed"
          ? "Google sign-in could not be completed. Please try again."
          : null;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const me = await authApi.login(email, password);
      setMe(me);
      navigate({ to: search.redirect ?? "/" });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.status === 429
            ? "Too many attempts. Wait a minute and try again."
            : err.message
          : "Could not reach the server. Check your connection.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-5 rounded-2xl border border-[var(--border)] bg-[var(--bg)] p-6 shadow-sm"
        noValidate
      >
        <header className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          <p className="text-sm opacity-60">Biz360 · CRM & ERP</p>
        </header>

        {needsSetup && (
          <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
            This installation has no Owner yet. {" "}
            <Link to="/setup" search={{}} className="font-medium underline underline-offset-2">
              Start secure setup
            </Link>
          </div>
        )}

        <a
          href={`/api/auth/google/start?next=${encodeURIComponent(search.redirect ?? "/")}`}
          className="flex min-h-11 w-full items-center justify-center gap-3 rounded-md border border-[var(--border)] bg-[var(--surface)] px-4 text-sm font-medium hover:bg-[var(--hover)]"
        >
          <span className="grid size-5 place-items-center rounded-full bg-white font-bold text-blue-600 shadow-sm">
            G
          </span>
          Continue with Google
        </a>

        <div className="flex items-center gap-3 text-xs opacity-45">
          <span className="h-px flex-1 bg-[var(--border)]" /> or use password
          <span className="h-px flex-1 bg-[var(--border)]" />
        </div>

        <div className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block opacity-80">Email</span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none ring-0 focus:border-[var(--accent)]"
              placeholder="you@company.com"
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block opacity-80">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            />
          </label>
        </div>

        {(error || googleError) && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {error || googleError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Signing in…
            </>
          ) : (
            <>
              <LogIn className="size-4" aria-hidden />
              Sign in
            </>
          )}
        </button>

        <div className="flex items-center justify-between text-xs">
          <Link to="/activate" search={{}} className="text-[var(--accent)]">
            Activate employee account
          </Link>
          <Link to="/forgot-password" className="text-[var(--accent)]">
            Forgot password?
          </Link>
        </div>
      </form>
    </div>
  );
}
